import type { Week } from './auto-place'

/**
 * Fills in `elapsed` for a week the mock board builds.
 *
 * The fixtures carry week labels rather than an engagement's real calendar
 * dates, so the badge is the only signal they have: `past` is behind us,
 * anything else is not. Derived here, once, rather than written per fixture —
 * a hand-written pair can drift into `past` but not elapsed, which the
 * persisted board cannot produce and which no test should be asserting
 * against.
 *
 * The pair the real board *can* produce — a published week that has already
 * elapsed — has no badge to derive it from, so a caller that needs one states
 * `elapsed` explicitly and that wins.
 */
export const withMockElapsed = (
  week: Omit<Week, 'elapsed' | 'clientActivity'> & {
    elapsed?: boolean
    clientActivity?: boolean
  },
): Week => ({
  ...week,
  elapsed: week.elapsed ?? week.status === 'past',
  // Derived from the cards, which is what `unpublishBlockedReason` itself used
  // to do for every board until LG-590 moved the real answer to the server.
  //
  // The derivation was wrong there and is right here: it is wrong against a
  // real engagement because two of the server's three sources are rows the
  // plan DTO never carries, and it is right for these fixtures because the
  // cards are all there is — there is no server behind the mock board to
  // disagree with. Keeping it means the mock board answers exactly as it did
  // before, rather than reporting every week as untouched.
  clientActivity:
    week.clientActivity ??
    week.days.some((day) =>
      day.cards.some(
        (card) =>
          card.done === true ||
          card.status === 'submitted' ||
          card.status === 'completed',
      ),
    ),
})
